import React from 'react';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import styled from '@emotion/styled';

const StyledRichText = styled.div`
	div,
	p,
	li,
	a {
		font-size: 16px;
		line-height: 24px;
		letter-spacing: 0.15px;

		&:empty {
			display: none;
		}
	}

	p,
	ul,
	ol,
	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		margin-bottom: 20px !important;
		&.mb-0 {
			margin-bottom: 0 !important;
		}
	}

	ul,
	ol {
		text-align: left !important;
		padding-left: 40px;

		p {
			margin-bottom: 9px !important;
		}
	}

	ul {
		list-style: disc !important;
	}

	.iframeLink {
		padding-top: 56.25%;

		iframe {
			position: absolute;
			left: 0;
			top: 0;
		}
	}

	.page-heading {
		padding-bottom: 12px;
		border-bottom: 1px solid #e3e3e3;
		h1 {
			letter-spacing: 0.15px;
			text-transform: unset;
			font-size: 24px;
			line-height: 32px;
		}
	}
`;

function RichTextRenderer({
	content,
	color,
	imageTitle = '',
	headingIcon: HeadingIcon
}) {
	let nodeLength = 0;

	if (content?.content.length > 0) {
		content?.content.map((copy) => {
			if (copy?.content[0]?.value) {
				nodeLength++;
			}
		});
	}

	const CUSTOM = ({ children }) => {
		// console.log(children, typeof children);
		if (
			typeof children[0] !== 'object' &&
			children[0].indexOf('<iframe ') > -1
		) {
			let iframeLink = children[0].split('src="')[1].split('"')[0];
			if (iframeLink.indexOf('youtube') > -1) {
				iframeLink = iframeLink + '?modestbranding=1&rel=0';
			}
			return (
				<p className='iframeLink position-relative'>
					<iframe
						src={iframeLink}
						className='position-absolute h-100'
						width='100%'
						height='400'
						frameBorder='0'
						allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
						allowFullScreen
					/>
				</p>
			);
		} else {
			return <p className={nodeLength === 1 ? 'mb-0' : ''}>{children}</p>;
		}
	};

	const options = {
		renderNode: {
			[BLOCKS.EMBEDDED_ASSET]: (node) => {
				return (
					<p>
						<img
							className='mw-100'
							src={node?.data?.target?.fields?.file?.url}
							alt={imageTitle}
							title={imageTitle}
						/>
					</p>
				);
			},
			[BLOCKS.PARAGRAPH]: (node, children) => <CUSTOM>{children}</CUSTOM>,
			[BLOCKS.HEADING_1]: (node, children) => (
				<div className='d-flex align-items-center page-heading mb-40'>
					<HeadingIcon className='mr-12' />
					<h1 className={`lower font-oswald mb-0`}>{children}</h1>
				</div>
			),
			[BLOCKS.HEADING_2]: (node, children) => (
				<h2 className={`lower ${nodeLength === 1 ? 'mb-0' : ''}`}>
					{children}
				</h2>
			),
			[BLOCKS.HEADING_3]: (node, children) => (
				<h3 className={`lower ${nodeLength === 1 ? 'mb-0' : ''}`}>
					{children}
				</h3>
			),
			[BLOCKS.HEADING_4]: (node, children) => (
				<h4 className={`lower ${nodeLength === 1 ? 'mb-0' : ''}`}>
					{children}
				</h4>
			),
			[BLOCKS.HEADING_5]: (node, children) => (
				<h5 className={`lower ${nodeLength === 1 ? 'mb-0' : ''}`}>
					{children}
				</h5>
			),
			[BLOCKS.HEADING_6]: (node, children) => (
				<h6 className={`lower ${nodeLength === 1 ? 'mb-0' : ''}`}>
					{children}
				</h6>
			),
			[INLINES.HYPERLINK]: (node, children) => {
				return (
					<a rel='noopener noreferrer' href={node.data.uri}>
						{children}
					</a>
				);
			}
		}
	};

	return (
		<StyledRichText color={color}>
			{documentToReactComponents(content, options)}
		</StyledRichText>
	);
}

export default RichTextRenderer;
